import React, { useContext, useEffect, useRef, useState } from 'react';
import { OverLayParameter } from '../../App';
import { useTracking } from '../../tools/analytics';
import { RequirementsProps } from '../../tools/context';
import { useTranslation } from '../../tools/i18n';
import { getCookies, getQueryString, replaceQueryString } from '../../tools/tools';
import { Button } from '../Button';
import { Picto } from '../Picto';

import { TravelPlanerResultsContext } from '../TravelPlanerResults/TravelPlanerResultsContext';

export interface ChatAgentProps {
  className?: string;
  requirements: RequirementsProps;
  hideTimer?: number;
  context?: any;
  agentId?: string;
  setOverlay: (p: OverLayParameter) => void;
  withLabel?: boolean;
  hideHighlight?: boolean;
  inputDefaultValue?: string;
}

export const ChatAgent = (props: ChatAgentProps) => {
  const {
    className,
    requirements,
    hideTimer,
    context,
    agentId,
    withLabel,
    hideHighlight,
    inputDefaultValue,
  } = props;
  const [showChat, setShowChat] = useState<boolean>(false);
  const [hideAgent, setHideAgent] = useState<boolean>(true);
  const [highLightMessage, setHighLightMessage] = useState<string>('');
  const [showHighLight, setShowHighLight] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>();
  const { trackEvent } = useTracking();
  const { t, language } = useTranslation();

  const { siteConfiguration, chatInputDefaultValue, shouldOpenChat, setChatInputDefaultValue } =
    useContext(TravelPlanerResultsContext);

  const [daysFooterElement, setDaysFooterElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDaysFooterElement(document.getElementById('daysFooter'));
    }
  }, []);

  useEffect(() => {
    const hideScroll = () => {
      if (showChat && window.innerWidth < 600) {
        if (typeof window !== 'undefined') {
          document.body.style.overflow = 'hidden';
          daysFooterElement?.setAttribute('style', 'z-index: 94');
        }
      } else {
        if (typeof window !== 'undefined') {
          document.body.style.overflow = 'auto';
          daysFooterElement?.setAttribute('style', 'z-index: 96');
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', hideScroll);
      hideScroll();

      return () => {
        document.body.style.overflow = 'auto';
        window.removeEventListener('resize', hideScroll);
      };
    }
  }, [showChat, daysFooterElement]);

  useEffect(() => {
    if (shouldOpenChat && chatInputDefaultValue) {
      const cookies = getCookies();
      if (!cookies.loginToken && requirements.config.travelForceLogin) {
        props.setOverlay({
          key: 'loginPopup',
          context: { queries: { chatbot: 'open' } },
        });
        return;
      }

      setShowChat(true);
      trackEvent('chatbot_open_click');

      setTimeout(() => {
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            {
              type: 'inputDefaultValue',
              payload: chatInputDefaultValue,
            },
            '*'
          );
        }
      }, 500);

      if (setChatInputDefaultValue) {
        setTimeout(() => {
          setChatInputDefaultValue('');
        }, 600);
      }
    }
  }, [
    shouldOpenChat,
    chatInputDefaultValue,
    requirements.config.travelForceLogin,
    props,
    trackEvent,
    setChatInputDefaultValue,
  ]);

  useEffect(() => {
    setTimeout(() => {
      setHideAgent(false);
    }, hideTimer || 0);

    const cookies = getCookies();
    if (
      getQueryString('chatbot') === 'open' &&
      (cookies.loginToken || !requirements.config.travelForceLogin)
    ) {
      setShowChat(true);
      replaceQueryString('chatbot', null);
    }

    const handleChatbotReady = (event: MessageEvent) => {
      if (event.data.payload) {
        setHighLightMessage(event.data.payload);
        setTimeout(() => setShowHighLight(!hideHighlight), 100);
        setTimeout(() => setShowHighLight(false), 5000);
        setTimeout(() => setHighLightMessage(''), 5150);
      }
      if (context) {
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: 'contextData',
            payload: context,
          },
          '*'
        );
      }
      if (inputDefaultValue) {
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: 'inputDefaultValue',
            payload: inputDefaultValue,
          },
          '*'
        );
      }
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: 'clearConversationId',
          payload: '',
        },
        '*'
      );
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: 'clearVisitorId',
          payload: '',
        },
        '*'
      );
    };

    window.addEventListener('message', (event) => {
      if (!event.data) return;
      if (event.data.type === 'chatbotReady') handleChatbotReady(event);
    });

    //const payload = 'Bonjour 👋🏼 Vous avez besoin de plus de précisions sur votre itinéraire ? N'hésitez pas à me poser vos questions 😊';
    const payload = t('travelPlaner.Travel.chatPopup');
    setHighLightMessage(payload);
    setTimeout(() => setShowHighLight(!hideHighlight), 100);
    setTimeout(() => setShowHighLight(false), 5000);
    setTimeout(() => setHighLightMessage(''), 5150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, hideHighlight, hideTimer, requirements.config.travelForceLogin]);

  if (hideAgent) return null;

  const cookies = getCookies();
  const handleButtonClick = () => {
    if (!cookies.loginToken && requirements.config.travelForceLogin) {
      props.setOverlay({
        key: 'loginPopup',
        context: { queries: { chatbot: 'open' } },
      });
      return;
    }
    setShowChat((p) => {
      if (!p) {
        trackEvent('chatbot_open_click');
      }
      return !p;
    });
    setHighLightMessage('');
    setShowHighLight(false);
  };

  const agentClassName = className || '';
  return (
    <div className={`agent ${agentClassName} ${withLabel ? 'withLabel' : ''}`}>
      {highLightMessage && (
        <div className={`highLight ${showHighLight ? 'visible' : ''}`}>{highLightMessage}</div>
      )}

      <Button
        onClick={() => handleButtonClick()}
        withPicto={'chat-icon'}
        textLabel="travelPlaner.Travel.chatLabel"
        withSVG={siteConfiguration.withSVG}
      ></Button>

      {(cookies.loginToken || !requirements.config.travelForceLogin) && (
        <div
          className={`agentBlock ${showChat ? 'visible' : ''}`}
          onClick={() => setShowChat(false)}
        >
          <iframe
            ref={(ref) => {
              iframeRef.current = ref;
            }}
            onClick={(e) => e.stopPropagation()}
            className="chatIframe"
            title="chatbot"
            src={`https://factory.wearegenial.com/agents/${agentId}/iframe?lang=${language}`}
          />
          <button className="close" onClick={() => setShowChat(false)} aria-label="Fermer">
            <Picto iconKey={'caret-down'} width="16" height="10" />
          </button>
        </div>
      )}
    </div>
  );
};
